import IconWarning from '@apollo/icons/default/IconWarning.svg';
import { Alert } from '@apollo/orbit';
import classnames from 'classnames';
import { GraphQLEnumValue, GraphQLField } from 'graphql';
import React from 'react';

import { EnumValueCoordinateSignature } from 'src/app/graph/insights/fieldPage/summary/fieldDefinition/enumValueCoordinateSignature/EnumValueCoordinateSignature';
import { FieldPageCoordinateSignature } from 'src/app/graph/insights/fieldPage/summary/fieldDefinition/fieldPageCoordinateSignature/FieldPageCoordinateSignature';
import { FieldType } from 'src/app/graph/insights/fieldPage/useCurrentSchemaCoordinate';
import { ShimmerLoading } from 'src/components/common/loading/ShimmerLoading';
import { CoordinateKind } from 'src/lib/graphqlTypes/types';

const notFoundTextByCoordinateKind = new Map<CoordinateKind, string>([
  [
    CoordinateKind.ENUM_VALUE,
    'This enum value was not found in the current schema.',
  ],
  [
    CoordinateKind.INPUT_OBJECT_FIELD,
    'This input field was not found in the current schema.',
  ],
  [
    CoordinateKind.OBJECT_FIELD,
    'This field was not found in the current schema.',
  ],
]);

interface Props {
  className?: string;
  field?: FieldType;
  isRootType: boolean;
  loading?: boolean;
  coordinateKind: CoordinateKind;
}

export const FieldDefinition = ({
  className,
  field,
  isRootType,
  coordinateKind,
  loading,
}: Props) => (
  <div className={classnames('flex-1 overflow-auto', className)}>
    {loading ? (
      <>
        <ShimmerLoading className="my-2 w-36" />
        <ShimmerLoading className="my-2 w-40" />
        <ShimmerLoading className="my-2 w-36" />
        {[1, 2].map((i: number) => (
          <ShimmerLoading key={i} className="my-2 w-44" />
        ))}
        <ShimmerLoading className="my-2 w-40" />
        {[1, 2].map((i: number) => (
          <ShimmerLoading key={i} className="my-2 w-36" />
        ))}
      </>
    ) : field ? (
      <>
        {field.deprecationReason && (
          <Alert className="mb-4" status="warning">
            DEPRECATED: {field?.deprecationReason}
          </Alert>
        )}

        {coordinateKind === CoordinateKind.ENUM_VALUE ? (
          <EnumValueCoordinateSignature
            isRootType={isRootType}
            enumValue={field as GraphQLEnumValue}
          />
        ) : (
          <FieldPageCoordinateSignature
            isRootType={isRootType}
            field={field as GraphQLField<unknown, unknown>}
            coordinateKind={coordinateKind}
          />
        )}
      </>
    ) : (
      <div className="mx-auto flex h-full w-52 flex-col items-center justify-center text-center">
        <IconWarning className="mb-4 text-icon-warning" />
        <p className="text-lg text-grey-darker">
          {notFoundTextByCoordinateKind.get(coordinateKind)}
        </p>
      </div>
    )}
  </div>
);
